import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  Container,
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
// import Slider from "react-slick";
import SliderWrapper from '../../../../../Components/SliderWrapper';
import type ReactSlick from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { masterlayoutbg } from '../../../../../assets';
import { GallerySection } from '../ProjectInterface';

interface ImageCardProps {
  images: { src: string; label: string }[];
  height: string | number;
}

interface CustomTabsProps {
  tab: number;
  setTab: (value: number) => void;
}
interface GalleryProps {
  gallerysection: GallerySection | null;
}

const CDN_URL =
  process.env.NEXT_PUBLIC_STORAGE_DN_URL || 'https://dprstorage.b-cdn.net';

const resolveImageUrl = (path: string) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${CDN_URL}${path}`;
};

const CustomTabs: React.FC<CustomTabsProps> = ({ tab, setTab }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
        }}
      >
        {["INTERIOR", "EXTERIOR"].map((label, index) => {
          const active = tab === index;

          return (
            <Box
              key={label}
              sx={{
                position: "relative",
              }}
            >
              <Box
                onClick={() => setTab(index)}
                sx={{
                  minWidth: {
                    xs: "70px",
                    md: "110px",
                  },

                  height: {
                    xs: "54px",
                    md: "45px",
                  },

                  px: 4,

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  borderRadius: "60px",

                  cursor: "pointer",

                  border: active
                    ? "1px solid silver"
                    : "1px solid rgba(255,255,255,0.12)",

                  background: active
                    ? "linear-gradient(135deg,#30779d,silver)"
                    : "rgba(255,255,255,0.05)",

                  color: "white",

                  backdropFilter: "blur(18px)",

                  fontWeight: 700,

                  letterSpacing: 2,

                  fontSize: {
                    xs: "13px",
                    md: "14px",
                  },

                  transition: "0.4s",

                  boxShadow: active
                    ? "0px 10px 35px rgba(48,119,157,0.4)"
                    : "none",

                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#30779d,silver)",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    letterSpacing: 2,
                    fontSize: {
                      xs: "13px",
                      md: "14px",
                    },
                  }}
                >
                  {label}
                </Typography>
              </Box>

              {/* ACTIVE ARROW */}
              {/* {active && (
                <Box
                  sx={{
                    position: "absolute",

                    left: "50%",

                    transform: "translateX(-50%)",

                    bottom: "-12px",

                    width: 0,

                    height: 0,

                    borderLeft:
                      "10px solid transparent",

                    borderRight:
                      "10px solid transparent",

                    borderTop:
                      "12px solid #30779d",
                  }}
                />
              )} */}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const Gallery: React.FC<GalleryProps> = ({ gallerysection }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);

  // ✅ Handle null or missing gallerysection
  // if (!gallerysection || (!gallerysection.interior && !gallerysection.exterior)) {
  //   return null; // or return a fallback UI here
  // }

  const currentGallery =
    tab === 0 ? gallerysection?.interior : gallerysection?.exterior;
  const layout = ['c0r0', 'c0r1', 'c1r0', 'c2r0', 'c2r1'];

  const images: { src: string; label: string }[][] = layout.map((key) => {
    const imgs = currentGallery?.[key as keyof typeof currentGallery] || [];
    return imgs.map((src, i) => ({ src, label: `Gallery Image ${i + 1}` }));
  });

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: 'auto',
        backgroundImage: `url(${masterlayoutbg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'bottom center',
        overflow: 'hidden',
        py: 6,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="xl">
        {/* Title */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 3,
            mb: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" fontWeight={600} color="white" mb={1}
          sx={{
            fontFamily: 'Futura,  sans-serif !important',
            letterSpacing:"2px"
          }}
          >
            GALLERY
          </Typography>
          <Box
            sx={{
              width: {    
                xs: "120px",
                md: "180px",
              },

              height: "2px",

              background:
                "linear-gradient(to right, transparent, #fff, transparent)",

              mx: "auto",

              mb: 3,
            }}
          />
        </Box>

        {/* Null fallback */}
        {!gallerysection ||
          (!gallerysection.interior && !gallerysection.exterior) ? (
          <Typography
            variant="body2"
            sx={{
              position: 'relative',
              zIndex: 3,
              textAlign: 'center',
              fontSize: '18px',
              color: 'white',
              fontWeight: 300,
              letterSpacing: 0.5,
              mt: 4,
            }}
          >
            Gallery data is not uploaded.
          </Typography>
        ) : (
          <>
            {/* Tabs */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 3,
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CustomTabs tab={tab} setTab={setTab} />
            </Box>

            {/* Image Grid */}
           {/* Image Grid */}

<Box
  sx={{
    display: "grid",

    gridTemplateColumns: {
      xs: "1fr",
      md: "0.9fr 1.1fr 0.9fr",
    },

    gap: {
      xs: 1.5,
      md: 2,
    },

    alignItems: "center",

    maxWidth: "1250px",

    mx: "auto",

    px: {
      xs: 1,
      md: 2,
    },

    position: "relative",

    zIndex: 2,
  }}
>
  {/* LEFT */}

  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <ImageCard
      images={images[0]}
      height={isMobile ? 180 : 200}
    />

    <ImageCard
      images={images[1]}
      height={isMobile ? 230 : 250}
    />
  </Box>

  {/* CENTER */}

  <Box>
    <ImageCard
      images={images[2]}
      height={isMobile ? 320 : 470}
    />
  </Box>

  {/* RIGHT */}

  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <ImageCard
      images={images[3]}
      height={isMobile ? 230 : 250}
    />

    <ImageCard
      images={images[4]}
      height={isMobile ? 180 : 200}
    />
  </Box>
</Box>
          </>
        )}
      </Container>
    </Box>
  );
};

const ImageCard: React.FC<ImageCardProps> = ({ images, height }) => {
  const [sliderKey, setSliderKey] = useState(0);
  const sliderRef = useRef<ReactSlick>(null);

  useEffect(() => {
    setSliderKey((prev) => prev + 1);
  }, [images]);

  const settings = {
    dots: images.length > 1, // Only show dots if more than one image
    arrows: false,
    infinite: images.length > 1,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2500,
    pauseOnHover: false,
    cssEase: 'linear',
    adaptiveHeight: false,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        height,
        width: '100%',
        zIndex: 3,
        '& .slick-dots': {
          bottom: 8,
          display: images.length > 1 ? 'flex !important' : 'none !important',
          justifyContent: 'center',
          gap: '2px',
          '& li': {
            margin: 0,
          },
          '& li button:before': {
            fontSize: 8,
            color: '#fff',
          },
          '& li.slick-active button:before': {
            color: 'rgb(7, 141, 243)',
          },
        },
      }}
    >
      <SliderWrapper ref={sliderRef} key={sliderKey} {...settings}>
        {images.map((img, i) => (
          <Box key={i} sx={{ position: 'relative', height, width: '100%' }}>
            <Box
              component="img"
              src={resolveImageUrl(img.src)}
              alt={img.label || 'Gallery Image'}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {/* <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                color: 'white',
                fontWeight: 600,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '14px',
                zIndex: 5,
              }}
            >
              {img.label}
            </Typography> */}
          </Box>
        ))}
      </SliderWrapper>
    </Box>
  );
};

export default Gallery;
